import { useMemo, useState } from 'react';
import { Plus, Pencil, Minus, ChevronDown, ChevronRight, Loader2, FileCode2 } from 'lucide-react';
import { useFileActivity } from '../../api/telemetry';
import type { FileEvent } from '../../api/telemetry';

interface Props {
  batchId: string | undefined;
  maxHeight?: string;
}

const opIcon: Record<FileEvent['operation'], { icon: typeof Plus; color: string }> = {
  created: { icon: Plus, color: 'text-emerald-500' },
  modified: { icon: Pencil, color: 'text-blue-500' },
  deleted: { icon: Minus, color: 'text-red-500' },
};

function cleanPath(filePath: string): string {
  // Strip common prefixes like /workspace/ or /home/coder/
  return filePath.replace(/^\/(workspace|home\/coder|app|src)\//i, '');
}

function relativeTime(timestamp: string): string {
  const diff = Date.now() - new Date(timestamp).getTime();
  const seconds = Math.floor(diff / 1000);
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

interface StoryGroup {
  storyId: string;
  files: FileEvent[];
}

export function FileActivityPanel({ batchId, maxHeight = '360px' }: Props) {
  const { data, isLoading } = useFileActivity(batchId);
  const [expandedStories, setExpandedStories] = useState<Set<string>>(new Set());

  const summary = useMemo(() => {
    if (!data?.files) return { total: 0, created: 0, modified: 0, deleted: 0 };
    return {
      total: data.files.length,
      created: data.files.filter(f => f.operation === 'created').length,
      modified: data.files.filter(f => f.operation === 'modified').length,
      deleted: data.files.filter(f => f.operation === 'deleted').length,
    };
  }, [data]);

  const storyGroups = useMemo((): StoryGroup[] => {
    if (!data?.files) return [];
    const groups = new Map<string, FileEvent[]>();
    for (const file of data.files) {
      const key = file.storyId || file.issueId || 'unknown';
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)!.push(file);
    }
    return Array.from(groups.entries())
      .map(([storyId, files]) => ({ storyId, files }))
      .sort((a, b) => b.files.length - a.files.length);
  }, [data]);

  const toggleStory = (storyId: string) => {
    setExpandedStories(prev => {
      const next = new Set(prev);
      if (next.has(storyId)) next.delete(storyId);
      else next.add(storyId);
      return next;
    });
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg border border-neutral-200 shadow-warm-sm p-6 flex items-center justify-center gap-2 text-neutral-400">
        <Loader2 size={14} className="animate-spin" />
        <span className="text-[11px]">Loading file activity...</span>
      </div>
    );
  }

  if (!data || data.files.length === 0) {
    return (
      <div className="bg-white rounded-lg border border-neutral-200 shadow-warm-sm p-6 text-center">
        <FileCode2 size={20} className="mx-auto text-neutral-300 mb-2" />
        <p className="text-[11px] text-neutral-400">No file activity yet</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-neutral-200 shadow-warm-sm overflow-hidden">
      <div className="px-4 py-2.5 border-b border-neutral-100 flex items-center justify-between">
        <p className="text-[9px] font-bold uppercase tracking-widest text-neutral-400">File Activity</p>
        <p className="text-[10px] text-neutral-500">
          {summary.total} file{summary.total !== 1 ? 's' : ''} changed
          <span className="text-neutral-300 mx-1">(</span>
          <span className="text-emerald-600">{summary.created} created</span>
          <span className="text-neutral-300 mx-0.5">,</span>
          <span className="text-blue-600"> {summary.modified} modified</span>
          <span className="text-neutral-300 mx-0.5">,</span>
          <span className="text-red-600"> {summary.deleted} deleted</span>
          <span className="text-neutral-300">)</span>
        </p>
      </div>

      <div className="overflow-y-auto" style={{ maxHeight }}>
        {storyGroups.map(({ storyId, files }) => {
          const isExpanded = expandedStories.has(storyId);
          return (
            <div key={storyId} className="border-b border-neutral-50 last:border-b-0">
              <button
                onClick={() => toggleStory(storyId)}
                className="w-full text-left flex items-center gap-2 px-4 py-2 hover:bg-neutral-50 transition-colors"
              >
                {isExpanded
                  ? <ChevronDown size={12} className="text-neutral-400 shrink-0" />
                  : <ChevronRight size={12} className="text-neutral-400 shrink-0" />
                }
                <span className="text-[11px] font-mono font-semibold text-neutral-600">{storyId}</span>
                <span className="text-[10px] text-neutral-400 ml-auto">{files.length} file{files.length !== 1 ? 's' : ''}</span>
              </button>

              {isExpanded && (
                <div className="pl-8 pr-4 pb-2 space-y-0.5">
                  {files.map(file => {
                    const op = opIcon[file.operation];
                    const Icon = op.icon;
                    return (
                      <div key={file.id} className="flex items-center gap-2 py-1 px-2 rounded hover:bg-neutral-50 transition-colors">
                        <Icon size={11} className={`${op.color} shrink-0`} />
                        <span className="text-[10px] font-mono text-neutral-600 truncate flex-1" title={file.filePath}>
                          {cleanPath(file.filePath)}
                        </span>
                        {file.language && (
                          <span className="text-[9px] font-medium px-1.5 py-0.5 rounded bg-neutral-100 text-neutral-500 shrink-0">
                            {file.language}
                          </span>
                        )}
                        <span className="text-[9px] text-neutral-400 shrink-0 tabular-nums">
                          {relativeTime(file.timestamp)}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
